import { useNavigate, useParams } from "react-router";
import t from "@/lang/lang";
import { useQpmRequirementDeleteMutation } from "@/services/backend/qpm/requirements/api/requirement-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { useEffect } from "react";
import { Loader2, Trash2 } from "lucide-react";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { useToast } from "@/shared/hooks/use-toast";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { RequirementRouteNav } from "@/routes/processes/[processId]/qpm/_components/nav-links/requirement-nav";
import {
  RequirementDataProvider,
  RequirementEditProvider,
  useRequirementData,
} from "@/services/backend/qpm/requirements/hooks/requirement-context";

export type URLParams = {
  processId: string;
  requirementId: string;
};

export default function RequirementLayout() {
  const { processId, requirementId } = useParams<URLParams>();

  const {
    data: process,
    error: processDataError,
    isLoading: processDataIsLoading,
  } = useProcessesProcessShowQuery({
    id: processId ?? "",
  });

  if (processDataIsLoading) {
    return <Loader2 className="animate-spin" />;
  }

  if (!process) {
    return <div>Fehler beim Laden des Vorgangs!</div>;
  }

  if (processDataError) {
    return <RTKQueryErrorAlert error={processDataError} />;
  }

  return (
    <PersistenceContextProvider>
      <RequirementDataProvider requirementId={requirementId!}>
        <RequirementLayoutEditWrapper />
      </RequirementDataProvider>
    </PersistenceContextProvider>
  );
}

function RequirementLayoutEditWrapper() {
  const { requirement, error } = useRequirementData();
  const links = [
    { to: RequirementRouteNav.Info, label: t("Details") },
    { to: RequirementRouteNav.Assessment, label: t("Bestandsaufnahme") },
    {
      to: RequirementRouteNav.AdditionalInfo,
      label: t("Zusätzliche Informationen"),
    },
    { to: RequirementRouteNav.TotalOverview, label: t("Gesamtübersicht") },
  ];

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    requirement && (
      <RequirementEditProvider requirement={requirement}>
        <RequirementLayoutContent links={links} />
      </RequirementEditProvider>
    )
  );
}

function RequirementLayoutContent({
  links,
}: {
  links: { to: string; label: string }[];
}) {
  const { requirement, isLoading } = useRequirementData();

  if (isLoading) {
    return <Loader2 className="animate-spin" />;
  }

  if (!requirement) {
    return <div>Error loading Requirement!</div>;
  }

  return (
    <DocumentPage>
      <DocumentPageHeader>
        <DocumentPageNavigation>
          {links.map((link) => (
            <DocumentPageNavigationLink to={link.to} key={link.to}>
              {link.label}
            </DocumentPageNavigationLink>
          ))}
        </DocumentPageNavigation>
        <DocumentPageActions>
          <PersistenceStateUsingContext className="mr-2" />
          {requirement && (
            <DeleteRequirementDialog requirementId={requirement.id} />
          )}
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}

function DeleteRequirementDialog(props: { requirementId: string }) {
  const { requirementId } = props;
  const [deleteCalculation, { isLoading, error, isSuccess, reset }] =
    useQpmRequirementDeleteMutation();
  const { toast } = useToast();
  const navigate = useNavigate();

  const onClick = () => {
    if (isLoading) {
      return;
    }
    deleteCalculation({ id: requirementId });
  };

  useEffect(() => {
    if (isSuccess) {
      toast({
        title: t("Bestandsaufnahme gelöscht"),
        variant: "success",
      });
      navigate(`..`);
      reset();
    }
  }, [error, isSuccess, navigate, reset, toast]);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Trash2 className="h-5 w-5" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Bestandsaufnahme wirklich löschen?")}</DialogTitle>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button variant="destructive" onClick={onClick}>
            {t("Löschen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
